// Moteur de recommandations — configuration et performance des recommandations produit
import { RefreshCw, ToggleLeft, ToggleRight, BarChart2 } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useRecommendations,
  useToggleRecommendation,
  useRetrainRecommendation,
} from "@/hooks/admin/useAI";
import type { RecommendationConfig, RecommendationPerformance, RecommendationType } from "@/lib/admin-api/ai";
import React, { useMemo } from "react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(n: number): string {
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

function conversionRate(conversions: number, clicks: number): string {
  if (clicks === 0) return "0%";
  return `${((conversions / clicks) * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Type label mapping
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<RecommendationType, string> = {
  frequently_bought_together: "Souvent achetés ensemble",
  similar_products: "Produits similaires",
  trending: "Tendances",
  personalized: "Personnalisées",
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Recommendations() {
  const { data, isLoading, isError } = useRecommendations();
  const toggleRec = useToggleRecommendation();
  const retrainRec = useRetrainRecommendation();

  const configs = data?.configs ?? [];
  const performance = data?.performance ?? [];

  const configColumns: readonly ColumnDef<RecommendationConfig>[] = useMemo(
    () => [
      {
        id: "type",
        header: "Type",
        cell: (row) => (
          <span className="text-sm font-medium text-stone-900">
            {TYPE_LABELS[row.type]}
          </span>
        ),
      },
      {
        id: "is_active",
        header: "Actif",
        cell: (row) => (
          <button
            type="button"
            disabled={toggleRec.isPending}
            onClick={() => toggleRec.mutate(row.id)}
            className="flex items-center gap-1.5 text-sm disabled:opacity-60"
            aria-label={row.is_active ? "Désactiver" : "Activer"}
          >
            {row.is_active ? (
              <>
                <ToggleRight className="h-5 w-5 text-green-600" />
                <span className="text-green-700">Actif</span>
              </>
            ) : (
              <>
                <ToggleLeft className="h-5 w-5 text-stone-400" />
                <span className="text-stone-400">Inactif</span>
              </>
            )}
          </button>
        ),
      },
      {
        id: "product_count",
        header: "Nb produits",
        cell: (row) => <span className="text-sm text-stone-700">{row.product_count}</span>,
      },
      {
        id: "last_updated_at",
        header: "Dernière MàJ",
        cell: (row) => (
          <span className="text-sm text-stone-500">{formatDate(row.last_updated_at)}</span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (row) => (
          <button
            type="button"
            disabled={retrainRec.isPending}
            onClick={() => retrainRec.mutate(row.id)}
            className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-200 disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${retrainRec.isPending ? "animate-spin" : ""}`} />
            Ré-entraîner
          </button>
        ),
      },
    ],
    [toggleRec, retrainRec],
  );

  const performanceColumns: readonly ColumnDef<RecommendationPerformance>[] = useMemo(
    () => [
      {
        id: "type",
        header: "Type",
        cell: (row) => (
          <span className="text-sm font-medium text-stone-900">{row.type}</span>
        ),
      },
      {
        id: "impressions",
        header: "Impressions",
        cell: (row) => (
          <span className="text-sm text-stone-700">{row.impressions.toLocaleString("fr-SN")}</span>
        ),
      },
      {
        id: "clicks",
        header: "Clics",
        cell: (row) => (
          <span className="text-sm text-stone-700">{row.clicks.toLocaleString("fr-SN")}</span>
        ),
      },
      {
        id: "conversions",
        header: "Conversions",
        cell: (row) => (
          <span className="text-sm font-medium text-stone-900">
            {row.conversions.toLocaleString("fr-SN")}
          </span>
        ),
      },
      {
        id: "revenue_fcfa",
        header: "CA généré",
        cell: (row) => (
          <span className="text-sm font-semibold text-stone-900">{formatFcfa(row.revenue_fcfa)}</span>
        ),
      },
      {
        id: "conversion_rate",
        header: "Taux conversion",
        cell: (row) => (
          <span className="text-sm font-medium text-stone-700">
            {conversionRate(row.conversions, row.clicks)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-8 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Moteur de recommandations</h1>
          <p className="mt-1 text-sm text-stone-500">
            Configuration et performance des recommandations produit générées par l'IA
          </p>
        </div>

        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les données. Veuillez réessayer.
          </div>
        )}

        {/* Configuration section */}
        <section>
          <h2 className="mb-4 text-base font-semibold text-stone-800">Configuration</h2>
          {configs.length === 0 && !isLoading ? (
            <EmptyState
              title="Aucune configuration"
              description="Les configurations de recommandation seront disponibles après initialisation."
            />
          ) : (
            <AdminDataTable
              data={configs}
              columns={configColumns}
              isLoading={isLoading}
              emptyMessage="Aucune configuration disponible."
            />
          )}
        </section>

        {/* Performance section */}
        <section>
          <div className="mb-4 flex items-center gap-2">
            <BarChart2 className="h-4 w-4 text-stone-400" />
            <h2 className="text-base font-semibold text-stone-800">Performance</h2>
          </div>
          {performance.length === 0 && !isLoading ? (
            <EmptyState
              title="Aucune donnée de performance"
              description="Les métriques de performance s'afficheront dès que les recommandations seront actives."
            />
          ) : (
            <AdminDataTable
              data={performance}
              columns={performanceColumns}
              isLoading={isLoading}
              emptyMessage="Aucune donnée de performance."
              getRowId={(row) => row.type}
            />
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
